import React, { useState } from 'react';
import { API_BASE } from '../../config';
import { useTranslation } from '../../translations';
import { useConfirm } from '../ui/Modal';
import { useToast } from '../ui/Toast';
import { formatDate, STREFA_FIRMY } from '../../utils/format';

// Okno „Przerwa” w panelu super-admina (FAZA F, F.11): włączenie przerwy w sezonie od 1. dnia
// następnego miesiąca (tak obiecuje cennik; `today` zostaje na trasie dla skryptów i testów, okno go
// nie wysyła), planowany koniec (tylko informacja dla firmy) i zakończenie przerwy.
// Reguły pilnuje serwer (Login/backend/lib/przerwaKonta.js), okno tylko je opisuje.
// Style: klasy z pages/SuperAdmin.css, jak DemoManageModal.

function csrfToken() {
  const parts = `; ${document.cookie}`.split('; csrf=');
  return parts.length === 2 ? parts.pop().split(';').shift() : '';
}

async function postJson(url, body) {
  const response = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken() },
    credentials: 'include',
    body: JSON.stringify(body),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw Object.assign(new Error(data.error || ''), { dane: data });
  return data;
}

/** 'brak' | 'zaplanowana' | 'trwa' z wiersza listy organizacji (status i paused_at). */
export function stanPrzerwyFirmy(org, teraz = new Date()) {
  if (!org || org.status !== 'paused') return 'brak';
  const od = org.paused_at ? new Date(org.paused_at) : null;
  return od && od.getTime() > teraz.getTime() ? 'zaplanowana' : 'trwa';
}

export default function PauseManageModal({ org, onClose, onChanged }) {
  const { t, currentLanguage } = useTranslation();
  const confirm = useConfirm();
  const toast = useToast();
  const stan = stanPrzerwyFirmy(org);
  const [until, setUntil] = useState(org.paused_until || '');
  const [busy, setBusy] = useState(false);
  // paused_at to znacznik czasu, którego dzień serwer liczy w strefie firmy.
  const data = (wartosc) => formatDate(wartosc, { locale: currentLanguage, fallback: '', timeZone: STREFA_FIRMY });
  const url = (koncowka) => `${API_BASE}/api/super-admin/organizations/${org.id}/${koncowka}`;

  const wykonaj = async (koncowka, cialo, komunikat) => {
    setBusy(true);
    try {
      try {
        await postJson(url(koncowka), cialo);
      } catch (e) {
        // Serwer liczy rezerwacje, których firma w przerwie nie obsłuży, i faktury, które nie trafiły
        // do KSeF (termin przesłania biegnie dalej), i pyta o potwierdzenie.
        const d = e.dane || {};
        if (d.code !== 'pause_open_reservations') throw e;
        const rezerwacje = (d.ongoingReservations || 0) + (d.upcomingReservations || 0);
        const czesci = [];
        if (d.unsentKsefInvoices > 0) czesci.push(t('pauseManage.unsentKsef', { liczba: d.unsentKsefInvoices }));
        czesci.push(rezerwacje > 0
          ? t('pauseManage.confirmOpenReservations', { trwajace: d.ongoingReservations || 0, nadchodzace: d.upcomingReservations || 0 })
          : t('pauseManage.confirmAnyway'));
        const ok = await confirm({ title: czesci.join(' '), tone: 'danger' });
        if (!ok) return;
        await postJson(url(koncowka), { ...cialo, confirmOpenReservations: true });
      }
      toast.success(komunikat);
      onChanged();
    } catch (e) {
      toast.error(e.message || t('pauseManage.error'));
    } finally {
      setBusy(false);
    }
  };

  const wlacz = async () => {
    const ok = await confirm({
      title: t('pauseManage.confirmNextMonth', { name: org.name }),
      tone: 'danger',
    });
    if (!ok) return;
    await wykonaj('pause', { start: 'next_month', until: until || null }, t('pauseManage.enabled'));
  };

  const zapiszKoniec = () => wykonaj('pause', { until: until || null }, t('pauseManage.untilSaved'));

  const zakoncz = async () => {
    const ok = await confirm({
      title: stan === 'trwa' ? t('pauseManage.confirmResume', { name: org.name }) : t('pauseManage.confirmCancel', { name: org.name }),
    });
    if (!ok) return;
    await wykonaj('resume', {}, t('pauseManage.resumed'));
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()} data-cy="pause-manage-modal">
        <div className="modal-header">
          <h2>{t('pauseManage.title', { name: org.name })}</h2>
          <button className="close-btn" onClick={onClose} aria-label={t('common.cancel')}>×</button>
        </div>

        <div className="org-form">
          {stan === 'brak' ? (
            <>
              <p><small>{t('pauseManage.explain')}</small></p>
              <p data-cy="pause-start-info">{t('pauseManage.startInfo')}</p>
            </>
          ) : (
            <p data-cy="pause-manage-state">
              {stan === 'trwa'
                ? t('pauseManage.stateActive', { od: data(org.paused_at) })
                : t('pauseManage.stateScheduled', { od: data(org.paused_at) })}
            </p>
          )}

          <div className="form-group">
            <label htmlFor="pause-until">{t('pauseManage.untilLabel')}</label>
            <input id="pause-until" type="date" value={until} onChange={(e) => setUntil(e.target.value)} data-cy="pause-until" />
            <small>{t('pauseManage.untilHint')}</small>
          </div>

          <div className="modal-actions">
            <button type="button" className="btn-secondary" onClick={onClose}>{t('common.cancel')}</button>
            {stan === 'brak' && (
              <button type="button" className="btn-primary" onClick={wlacz} disabled={busy} data-cy="pause-enable">
                {t('pauseManage.enable')}
              </button>
            )}
            {stan !== 'brak' && (
              <>
                <button type="button" className="btn-secondary" onClick={zapiszKoniec} disabled={busy} data-cy="pause-save-until">
                  {t('pauseManage.saveUntil')}
                </button>
                <button type="button" className="btn-primary" onClick={zakoncz} disabled={busy} data-cy="pause-resume">
                  {stan === 'trwa' ? t('pauseManage.resume') : t('pauseManage.cancelScheduled')}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
